import React, { useState, useEffect } from 'react';
import { User, Book, GraduationCap, School, Heart, Star, Target, AlertTriangle, Phone, Mail} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import emailjs from '@emailjs/browser';

const PreCounselingForm = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const providedRegNo = queryParams.get('regNo');
    const regNo = providedRegNo || '';

    const [isValidRegNo, setIsValidRegNo] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [validationMessage, setValidationMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [formData, setFormData] = useState({
      name: '',
      age: '',
      phoneNumber: '',
      email: '',
      classYear: '',
      electiveSubjects: '',
      educationDegree: '',
      boardOfStudy: '',
      academicScore: '',
      parentsOccupation: '',
      areasOfInterest: '',
      interestSubjects: '',
      hobbies: '',
      coCurricular: '',
      preferredCareerCandidate: '',
      preferredCareerParent: '',
      counselingExpectation: '',
      referralSource: ''
    });

    const validateForm = () => {
      const errors = {};
      
      if (!formData.name.trim()) {
          errors.name = 'Name is required';
      }
  
      if (!formData.age) {
          errors.age = 'Age is required';
      } else if (isNaN(formData.age) || formData.age < 13 || formData.age > 50) {
          errors.age = 'Please enter a valid age between 13 and 50';
      }
  
      if (!formData.phoneNumber.trim()) {
          errors.phoneNumber = 'Phone number is required';
      } else {
          const phoneRegex = /^(?:(?:\+|0{0,2})91(\s*[-]\s*)?|[0]?)?[6789]\d{9}$/;
          if (!phoneRegex.test(formData.phoneNumber)) {
              errors.phoneNumber = 'Please enter a valid Indian phone number';
          }
      }
  
      if (!formData.email.trim()) {
          errors.email = 'Email is required';
      } else {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(formData.email)) {
              errors.email = 'Please enter a valid email address';
          }
      }
  
      return errors;
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      
      const errors = validateForm();
      setValidationErrors(errors);
      
      if (Object.keys(errors).length > 0) {
        return;
      }
    
      setIsSubmitting(true);
      setSubmitStatus(null);
    
      try {
        const submissionDate = new Date().toLocaleString('en-US', { 
          timeZone: 'Asia/Kolkata',
          dateStyle: 'full',
          timeStyle: 'long'
        });
        
        const templateParams = {
          to_email: 'abhirupdutta206@gmail.com',
          registration_number: regNo,
          submission_date: submissionDate,
          client_name: formData.name,
          age: formData.age,
          phone_number: formData.phoneNumber,
          email: formData.email,
          class_year: formData.classYear,
          elective_subjects: formData.electiveSubjects,
          education: formData.educationDegree,
          board: formData.boardOfStudy,
          academic_score: formData.academicScore,
          parents_occupation: formData.parentsOccupation,
          areas_of_interest: formData.areasOfInterest,
          interest_subjects: formData.interestSubjects,
          hobbies: formData.hobbies,
          co_curricular: formData.coCurricular,
          preferred_career_candidate: formData.preferredCareerCandidate,
          preferred_career_parent: formData.preferredCareerParent,
          counseling_expectation: formData.counselingExpectation,
          referral_source: formData.referralSource
        };

        await emailjs.send(
          'service_km0i2ak',
          'template_gywaafs',
          templateParams,
          '623kwrJgAFBG_e5QS'
        );

        if (regNo) {
          const existingRegs = JSON.parse(localStorage.getItem('registrationNumbers') || '[]');
          if (!existingRegs.includes(regNo)) {
            existingRegs.push(regNo);
            localStorage.setItem('registrationNumbers', JSON.stringify(existingRegs));
          }
        }

        setSubmitStatus('success');
        setFormData({
          name: '',
          age: '',
          phoneNumber: '',
          email: '',
          classYear: '',
          electiveSubjects: '',
          educationDegree: '',
          boardOfStudy: '',
          academicScore: '',
          parentsOccupation: '',
          areasOfInterest: '',
          interestSubjects: '',
          hobbies: '',
          coCurricular: '',
          preferredCareerCandidate: '',
          preferredCareerParent: '',
          counselingExpectation: '',
          referralSource: ''
        });
        
      } catch (error) {
        console.error('Error submitting form:', error);
        setSubmitStatus('error');
      } finally {
        setIsSubmitting(false);
      }
    };

    useEffect(() => {
      if (!providedRegNo) {
        setIsValidRegNo(false);
        setValidationMessage('No registration number provided');
        return;
      }

      const regNoFormat = /^\d{4}NCC\/\d{2}$/;
      if (!regNoFormat.test(providedRegNo)) {
        setIsValidRegNo(false);
        setValidationMessage('Invalid registration number format');
        return;
      }

      setIsValidRegNo(true);
      setValidationMessage('');
    }, [providedRegNo]);

    if (!isValidRegNo) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full bg-white shadow-xl rounded-lg p-8 text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Access</h2>
            <p className="text-gray-600 mb-4">{validationMessage}</p>
            <p className="text-sm text-gray-500">
              Please contact Nirvana Career Counselling to receive a valid registration number.
            </p>
          </div>
        </div>
      );
    }

    const renderFormField = (icon, label, name, type = "text", required = false) => {
      const Icon = icon;
      return (
        <div className="mb-4" key={name}>
          <label className="flex items-center text-gray-700 text-sm font-bold mb-2">
            <Icon className="w-4 h-4 mr-2 text-teal-600" />
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <input
            type={type}
            name={name}
            value={formData[name]}
            onChange={(e) => {
              const newValue = e.target.value;
              setFormData(prev => ({
                ...prev,
                [name]: newValue
              }));
            }}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
              validationErrors[name] ? 'border-red-500 bg-red-50' : 'border-teal-700'
            }`}
          />
          {validationErrors[name] && (
            <p className="mt-1 text-sm text-red-600">{validationErrors[name]}</p>
          )}
        </div>
      );
    };

    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto bg-white shadow-xl rounded-lg">
          <div className="bg-teal-600 text-white rounded-t-lg p-6">
            <h2 className="text-2xl font-bold text-center mb-4">Pre-Counseling Details</h2>
            <div className="mt-2 bg-teal-700 rounded-lg p-3 max-w-xs mx-auto">
              <p className="text-center text-sm text-white text-opacity-90">Registration Number</p>
              <p className="text-center font-mono font-bold">{regNo}</p>
            </div>
          </div>
          
          <div className="bg-teal-50 border-b border-teal-100 p-4">
            <p className="text-center text-teal-800 text-sm">
              Please fill out this form with accurate information to help us better understand your background and aspirations. <br/> 
              If any field is not relevant, please mark as <b>'N/A'</b>.
            </p>
          </div>

          <div className="p-6">
            {submitStatus === 'success' && (
              <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg">
                Thank you for submitting your details! We'll be in touch shortly.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
                Sorry, there was an error submitting your form. Please try again or contact us directly.
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {renderFormField(User, "Full Name", "name", "text", true)}
                {renderFormField(User, "Age", "age", "number", true)}
                {renderFormField(Phone, "Phone Number", "phoneNumber", "text", true)}
                {renderFormField(Mail, "Email", "email", "email", true)}
                {renderFormField(School, "Class/Graduation Year", "classYear")}
                {renderFormField(Book, "Elective Subjects", "electiveSubjects")}
                {renderFormField(GraduationCap, "Education Degree", "educationDegree")}
                {renderFormField(School, "Board of Study", "boardOfStudy")}
                {renderFormField(Star, "Academic Score (Latest)", "academicScore")}
                {renderFormField(User, "Parents Occupation", "parentsOccupation")}
                {renderFormField(User, "Where did you hear about Nirvana?", "referralSource")}
                
                <div className="md:col-span-2">
                  {renderFormField(Heart, "Areas of Interest", "areasOfInterest")}
                </div>
                
                <div className="md:col-span-2">
                  {renderFormField(Book, "Interest in Specific Subjects", "interestSubjects")}
                </div>
                
                <div className="md:col-span-2">
                  {renderFormField(Heart, "Hobbies", "hobbies")}
                </div>
                
                <div className="md:col-span-2">
                  {renderFormField(Star, "Co-Curricular Areas", "coCurricular")}
                </div>
                
                <div className="md:col-span-2">
                  {renderFormField(Target, "Preferred Career Option (Candidate)", "preferredCareerCandidate")}
                </div>
                
                <div className="md:col-span-2">
                  {renderFormField(Target, "Preferred Career Option (Parent)", "preferredCareerParent")}
                </div>
                
                <div className="md:col-span-2">
                  {renderFormField(Target, "Expectation through this counseling", "counselingExpectation")}
                </div>
              </div>

              <div className="flex justify-center mt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-6 py-3 bg-teal-600 text-white rounded-md transition-colors ${
                    isSubmitting 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:bg-teal-700'
                  }`}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Form'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
};

export default PreCounselingForm;